import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Plus, Search, Edit, Trash2, Mail, Phone, Globe, User, X, Sparkles,
  Download, Upload, Filter, ArrowDownLeft, ArrowUpRight,
  ScanSearch, Contrast, ArrowDownWideNarrow, Loader2, Tag, Check, Pencil,
  MoreHorizontal, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { exportContactsToCSV, parseContactImportFile, parseWebsiteImportFile, parseEmailWebsiteImportFile } from '../utils/export';
import { guessGenderFromName } from '../utils/guessGender';
import { PageSkeleton, ModalSkeleton } from './Skeleton';
import ContactImageImport from './ContactImageImport';
import ContactCsvImport from './ContactCsvImport';
import { useEmailHtmlDisplay } from '../hooks/useEmailHtmlDisplay';
import { stripOpenTrackingPixels } from '../utils/mailPreferences';
import SelectField from './SelectField';
import ContactDetailModal from './ContactDetailModal';
import { detectMailProvider, emailDomain } from '../utils/mailProviders';
import { isCompanyAsContactName } from '../utils/personName';
import { parseMailDate } from '../utils/mailDates';
import { isContactUnsubscribed, isContactBounced, formatUnsubscribedAt, withNormalizedSubscription } from '../utils/contactUnsubscribe';

const CONTACTS_CHANGED_EVENT = 'odium:contacts-changed';
const CONTACTS_PAGE_SIZE = 48;
/** Erste Seite + paralleles Hintergrund-Nachladen. */
const CONTACTS_FETCH_PAGE = 400;
const CONTACTS_FETCH_PARALLEL = 4;
const CATEGORY_COLOR_PRESETS = [
  '#0F766E',
  '#1D4ED8',
  '#7C3AED',
  '#BE185D',
  '#C2410C',
  '#A16207',
  '#475569',
  '#15803D',
];

const ContactCategoryChips = ({ categories = [], max = 3, className = '' }) => {
  const list = Array.isArray(categories) ? categories.filter(Boolean) : [];
  if (!list.length) return null;
  const shown = list.slice(0, max);
  const more = list.length - shown.length;
  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`}>
      {shown.map((cat) => (
        <span
          key={cat.id || cat.name}
          className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium max-w-[7rem] truncate"
          style={{
            backgroundColor: `${cat.color || '#0F766E'}22`,
            color: cat.color || '#0F766E',
          }}
          title={cat.name}
        >
          <span
            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: cat.color || '#0F766E' }}
          />
          <span className="truncate">{cat.name}</span>
        </span>
      ))}
      {more > 0 ? (
        <span className="text-[10px] text-neutral-400">+{more}</span>
      ) : null}
    </div>
  );
};

const notifyContactsChanged = () => {
  window.dispatchEvent(new CustomEvent(CONTACTS_CHANGED_EVENT));
};

/** Freemail-/Consumer-Domains, aus denen keine Firmenwebsite ableitbar ist */
const FREE_EMAIL_DOMAINS = new Set([
  'yahoo.com', 'yahoo.de', 'aol.com', 'proton.me', 'protonmail.com',
  't-online.de', 'freenet.de', 'online.de', 'arcor.de', 'mail.de',
  'posteo.de', 'mailbox.org', 'gmx.com', 'hotmail.de', 'live.de',
  'outlook.de', 'web.de', 'gmx.de', 'gmx.net', 'gmx.at', 'gmx.ch',
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com',
  'msn.com', 'icloud.com', 'me.com', 'mac.com',
]);

const historyItemMotion = {
  initial: false,
  animate: { opacity: 1, x: 0, height: 'auto' },
  exit: {
    opacity: 0,
    x: -18,
    height: 0,
    marginTop: 0,
    marginBottom: 0,
    transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
  },
  transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
};

const formatDateLong = (dateString) => {
  if (!dateString) return '—';
  const date = parseMailDate(dateString);
  if (!date) return '—';
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const plainTextToHtml = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
};

const getInitials = (nameOrEmail) => {
  if (!nameOrEmail) return '?';
  const base = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail;
  const parts = base.trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};

const Contacts = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const contactsActive = location.pathname === '/contacts';
  const { isDarkMode, invertEmailInDark, toggleEmailInvert, emailHtmlClassName } = useEmailHtmlDisplay();
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMoreContacts, setLoadingMoreContacts] = useState(false);
  const contactsFillGenerationRef = useRef(0);
  const [importingCsv, setImportingCsv] = useState(false);
  const [importingWebsiteCsv, setImportingWebsiteCsv] = useState(false);
  const [importingEmailWebsiteCsv, setImportingEmailWebsiteCsv] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [missingFilter, setMissingFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [categories, setCategories] = useState([]);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showFormNewCategory, setShowFormNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState(CATEGORY_COLOR_PRESETS[0]);
  const [categoryManagerBusy, setCategoryManagerBusy] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryColor, setEditCategoryColor] = useState(CATEGORY_COLOR_PRESETS[0]);
  const [contactsPage, setContactsPage] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    website: '',
    notes: '',
    gender: 'unknown',
    name2: '',
    gender2: 'unknown',
    categoryIds: [],
  });
  const [genderManual, setGenderManual] = useState(false);
  const [gender2Manual, setGender2Manual] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [impressumText, setImpressumText] = useState('');
  const [impressumHtml, setImpressumHtml] = useState('');
  const [showFullText, setShowFullText] = useState(false);
  const [showHtml, setShowHtml] = useState(false);
  const fileInputRef = React.useRef();

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyContact, setHistoryContact] = useState(null);
  const [historyMessages, setHistoryMessages] = useState([]);
  const [historyMeta, setHistoryMeta] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  const [detailContact, setDetailContact] = useState(null);
  const [showImageImport, setShowImageImport] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const mobileActionsBtnRef = React.useRef(null);
  const [mobileActionsPos, setMobileActionsPos] = useState(null);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvImportMode, setCsvImportMode] = useState('full'); // full | website | emailWebsite
  const [csvPreviewRows, setCsvPreviewRows] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [contextMenu, setContextMenu] = useState(null); // { x, y, contact }
  const [contactsToDelete, setContactsToDelete] = useState(null); // Contact[] | null
  const [deletingContacts, setDeletingContacts] = useState(false);
  const [historyEmailToDelete, setHistoryEmailToDelete] = useState(null);
  const [deletingHistoryEmail, setDeletingHistoryEmail] = useState(false);
  const websiteFileInputRef = React.useRef();
  const emailWebsiteFileInputRef = React.useRef();
  const anyCsvImporting = importingCsv || importingWebsiteCsv || importingEmailWebsiteCsv;

  const fetchContacts = useCallback(async ({ quiet = false } = {}) => {
    const generation = ++contactsFillGenerationRef.current;
    const normalizePage = (payload) => {
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.contacts)
          ? payload.contacts
          : [];
      return rows.map((c) => withNormalizedSubscription(c));
    };

    try {
      const firstRes = await axios.get('/api/contacts', {
        params: { limit: CONTACTS_FETCH_PAGE, offset: 0 },
        timeout: 60000,
      });
      if (contactsFillGenerationRef.current !== generation) return;

      const firstPayload = firstRes.data;
      const firstList = normalizePage(firstPayload);
      const totalHint = Number(
        Array.isArray(firstPayload) ? firstList.length : firstPayload?.total
      );
      const total =
        Number.isFinite(totalHint) && totalHint >= 0 ? totalHint : firstList.length;

      setContacts(firstList);
      setContactsTotal(total);
      setLoading(false);

      let hasMore = Array.isArray(firstPayload)
        ? false
        : firstPayload?.hasMore != null
          ? !!firstPayload.hasMore
          : firstList.length < total;

      if (!hasMore) {
        setLoadingMoreContacts(false);
        return;
      }

      setLoadingMoreContacts(true);

      const offsets = [];
      if (Number.isFinite(total) && total > firstList.length) {
        for (let o = firstList.length; o < total; o += CONTACTS_FETCH_PAGE) {
          offsets.push(o);
        }
      } else {
        // total unbekannt → sequenziell mit hasMore
        let offset = firstList.length;
        while (hasMore && contactsFillGenerationRef.current === generation) {
          const pageRes = await axios.get('/api/contacts', {
            params: { limit: CONTACTS_FETCH_PAGE, offset, skipTotal: 1 },
            timeout: 60000,
          });
          if (contactsFillGenerationRef.current !== generation) return;
          const chunk = normalizePage(pageRes.data);
          if (!chunk.length) break;
          setContacts((prev) => {
            const seen = new Set(prev.map((c) => Number(c.id)));
            const add = chunk.filter((c) => !seen.has(Number(c.id)));
            return add.length ? [...prev, ...add] : prev;
          });
          offset += chunk.length;
          hasMore = !!pageRes.data?.hasMore;
        }
        return;
      }

      for (let i = 0; i < offsets.length; i += CONTACTS_FETCH_PARALLEL) {
        if (contactsFillGenerationRef.current !== generation) return;
        const batch = offsets.slice(i, i + CONTACTS_FETCH_PARALLEL);
        const pages = await Promise.all(
          batch.map((offset) =>
            axios.get('/api/contacts', {
              params: { limit: CONTACTS_FETCH_PAGE, offset, skipTotal: 1 },
              timeout: 60000,
            })
          )
        );
        if (contactsFillGenerationRef.current !== generation) return;

        const mergedAdd = [];
        const seenInBatch = new Set();
        for (const pageRes of pages) {
          for (const c of normalizePage(pageRes.data)) {
            const id = Number(c.id);
            if (!id || seenInBatch.has(id)) continue;
            seenInBatch.add(id);
            mergedAdd.push(c);
          }
        }
        if (!mergedAdd.length) continue;

        setContacts((prev) => {
          const seen = new Set(prev.map((c) => Number(c.id)));
          const add = mergedAdd.filter((c) => !seen.has(Number(c.id)));
          return add.length ? [...prev, ...add] : prev;
        });
      }

      if (contactsFillGenerationRef.current === generation) {
        setContactsTotal(total);
      }
    } catch (error) {
      if (!quiet && contactsFillGenerationRef.current === generation) {
        toast.error('Fehler beim Laden der Kontakte');
      }
    } finally {
      if (contactsFillGenerationRef.current === generation) {
        setLoading(false);
        setLoadingMoreContacts(false);
      }
    }
  }, []);

  const fetchCategories = useCallback(async ({ quiet = false } = {}) => {
    try {
      const res = await axios.get('/api/contact-categories');
      setCategories(Array.isArray(res.data?.categories) ? res.data.categories : []);
    } catch (_) {
      if (!quiet) toast.error('Kategorien konnten nicht geladen werden');
    }
  }, []);

  useEffect(() => {
    if (!mobileActionsOpen) {
      setMobileActionsPos(null);
      return undefined;
    }
    const updatePos = () => {
      const el = mobileActionsBtnRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMobileActionsPos({
        top: Math.round(rect.bottom + 6),
        right: Math.round(Math.max(8, window.innerWidth - rect.right)),
      });
    };
    updatePos();
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileActionsOpen(false);
    };
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileActionsOpen]);

  // Auch im Hintergrund (Keep-alive) auf Änderungen hören, z. B. nach Firmensuche
  useEffect(() => {
    const onChanged = () => {
      fetchContacts({ quiet: true });
      fetchCategories({ quiet: true });
    };
    window.addEventListener(CONTACTS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CONTACTS_CHANGED_EVENT, onChanged);
  }, [fetchContacts, fetchCategories]);

  // Beim erneuten Öffnen der Seite neu laden
  useEffect(() => {
    if (!contactsActive) {
      contactsFillGenerationRef.current += 1;
      setLoadingMoreContacts(false);
      return;
    }
    fetchContacts({ quiet: true });
    fetchCategories({ quiet: true });
  }, [contactsActive, fetchContacts, fetchCategories]);

  useEffect(() => {
    if (!contextMenu) return undefined;
    const close = () => setContextMenu(null);
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  const openContextMenu = (e, contact) => {
    e.preventDefault();
    e.stopPropagation();
    const menuW = 200;
    const menuH = 140;
    const x = Math.min(e.clientX, window.innerWidth - menuW - 8);
    const y = Math.min(e.clientY, window.innerHeight - menuH - 8);
    setContextMenu({ x: Math.max(8, x), y: Math.max(8, y), contact });
  };

  const openContactDetail = (contact) => {
    if (!contact) return;
    setDetailContact(contact);
  };

  const closeContactDetail = () => {
    setDetailContact(null);
  };

  // Firmensuche / Deep-Link: /contacts?open=<id>[&history=1|&edit=1]
  useEffect(() => {
    if (!contactsActive || loading) return;
    const openId = Number(searchParams.get('open'));
    if (!openId) return;
    const wantHistory = searchParams.get('history') === '1';
    const wantEdit = searchParams.get('edit') === '1';
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    next.delete('history');
    next.delete('edit');
    setSearchParams(next, { replace: true });

    const apply = (match) => {
      if (wantEdit) {
        handleEdit(match);
        return;
      }
      if (wantHistory) {
        openContactHistory(match);
        return;
      }
      openContactDetail(match);
    };

    const match = contacts.find((c) => Number(c.id) === openId);
    if (match) {
      apply(match);
    } else {
      axios
        .get(`/api/contacts/${openId}`)
        .then((res) => {
          if (res.data?.id) apply(res.data);
          else toast.error('Kontakt nicht gefunden');
        })
        .catch(() => toast.error('Kontakt nicht gefunden'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsActive, loading, contacts, searchParams]);

  const companies = useMemo(() => {
    const set = new Set(contacts.map((c) => c.company).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }));
  }, [contacts]);

  const isBlank = (v) => !String(v || '').trim();
  const isGenderMissing = (g) => {
    const v = String(g || '').trim().toLowerCase();
    return !v || v === 'unknown';
  };

  const filteredContacts = useMemo(() => {
    const q = searchTerm.toLowerCase();
    let list = contacts.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.company && c.company.toLowerCase().includes(q)) ||
      (c.phone && c.phone.includes(searchTerm))
    );

    if (filterCategory === '__none__') {
      list = list.filter((c) => !Array.isArray(c.categories) || c.categories.length === 0);
    } else if (filterCategory) {
      const catId = Number(filterCategory);
      list = list.filter((c) =>
        (c.categories || []).some((cat) => Number(cat.id) === catId)
      );
    }

    if (missingFilter === 'company') {
      list = list.filter((c) => isBlank(c.company));
    } else if (missingFilter === 'phone') {
      list = list.filter((c) => isBlank(c.phone));
    } else if (missingFilter === 'website') {
      list = list.filter((c) => isBlank(c.website));
    } else if (missingFilter === 'gender') {
      list = list.filter((c) => isGenderMissing(c.gender));
    } else if (missingFilter === 'any') {
      list = list.filter(
        (c) =>
          isBlank(c.company) ||
          isBlank(c.phone) ||
          isBlank(c.website) ||
          isGenderMissing(c.gender)
      );
    }

    const collator = new Intl.Collator('de', { sensitivity: 'base', numeric: true });
    const byName = (a, b) => collator.compare(a.name || '', b.name || '');
    const byCompany = (a, b) => collator.compare(a.company || '', b.company || '') || byName(a, b);
    const byEmail = (a, b) => collator.compare(a.email || '', b.email || '');
    const byNewest = (a, b) =>
      (parseMailDate(b.created_at || b.updated_at)?.getTime() || 0) -
      (parseMailDate(a.created_at || a.updated_at)?.getTime() || 0);

    const sorted = [...list];
    switch (sortBy) {
      case 'name-desc':
        sorted.sort((a, b) => byName(b, a));
        break;
      case 'company-asc':
        sorted.sort(byCompany);
        break;
      case 'email-asc':
        sorted.sort(byEmail);
        break;
      case 'newest':
        sorted.sort(byNewest);
        break;
      case 'name-asc':
      default:
        sorted.sort(byName);
        break;
    }
    return sorted;
  }, [contacts, searchTerm, filterCategory, missingFilter, sortBy]);

  const hasActiveFilters = !!(searchTerm || filterCategory || missingFilter);

  const contactsTotalPages = Math.max(1, Math.ceil(filteredContacts.length / CONTACTS_PAGE_SIZE));
  const contactsPageSafe = Math.min(contactsPage, contactsTotalPages);

  const paginatedContacts = useMemo(() => {
    const start = (contactsPageSafe - 1) * CONTACTS_PAGE_SIZE;
    return filteredContacts.slice(start, start + CONTACTS_PAGE_SIZE);
  }, [filteredContacts, contactsPageSafe]);

  const pageSelectedCount = useMemo(
    () => paginatedContacts.filter((c) => selectedIds.has(c.id)).length,
    [paginatedContacts, selectedIds]
  );
  const allPageSelected =
    paginatedContacts.length > 0 && pageSelectedCount === paginatedContacts.length;

  useEffect(() => {
    setContactsPage(1);
  }, [searchTerm, filterCategory, missingFilter, sortBy]);

  useEffect(() => {
    if (contactsPage > contactsTotalPages) setContactsPage(contactsTotalPages);
  }, [contactsPage, contactsTotalPages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) { toast.error('Name und E-Mail sind Pflichtfelder'); return; }
    try {
      if (editingContact) {
        await axios.put(`/api/contacts/${editingContact.id}`, formData);
        toast.success('Kontakt aktualisiert');
      } else {
        await axios.post('/api/contacts', formData);
        toast.success('Kontakt erstellt');
      }
      fetchContacts();
      notifyContactsChanged();
      resetForm();
    } catch (error) {
      if (error.response?.data?.error?.includes('UNIQUE')) toast.error('Diese E-Mail ist bereits vorhanden');
      else toast.error('Fehler beim Speichern');
    }
  };

  const handleEdit = (c, e) => {
    if (e) e.stopPropagation();
    setEditingContact(c);
    setGenderManual(true);
    setFormData({
      name: c.name,
      email: c.email,
      company: c.company || '',
      phone: c.phone || '',
      website: c.website || '',
      notes: c.notes || '',
      gender: c.gender || 'unknown',
      name2: c.name2 || '',
      gender2: c.gender2 || 'unknown',
      categoryIds: (c.categories || []).map((cat) => cat.id),
    });
    setGenderManual(!!c.gender && c.gender !== 'unknown');
    setGender2Manual(!!c.name2 && !!c.gender2 && c.gender2 !== 'unknown');
    setShowModal(true);
  };

  const handleDelete = (id, e) => {
    if (e) e.stopPropagation();
    setContextMenu(null);
    const contact = contacts.find((c) => c.id === id);
    if (!contact) return;
    setContactsToDelete([contact]);
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    const list = contacts.filter((c) => selectedIds.has(c.id));
    if (!list.length) return;
    setContactsToDelete(list);
  };

  const confirmDeleteContacts = async () => {
    const list = contactsToDelete || [];
    if (!list.length || deletingContacts) return;
    setDeletingContacts(true);
    try {
      const results = await Promise.allSettled(
        list.map((c) => axios.delete(`/api/contacts/${c.id}`))
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const fail = results.length - ok;
      const ids = new Set(list.map((c) => c.id));

      if (ok > 0) {
        toast.success(
          ok === 1
            ? 'Kontakt gelöscht — Kampagnen gestoppt, gesendete Mails bleiben erhalten'
            : `${ok} Kontakte gelöscht — Kampagnen gestoppt, gesendete Mails bleiben erhalten`
        );
      }
      if (fail > 0) toast.error(`${fail} Kontakt${fail === 1 ? '' : 'e'} konnten nicht gelöscht werden`);

      if (detailContact && ids.has(detailContact.id)) closeContactDetail();
      if (historyContact && ids.has(historyContact.id)) {
        setHistoryOpen(false);
        setHistoryContact(null);
      }
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      setContactsToDelete(null);
      fetchContacts();
      notifyContactsChanged();
    } catch {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeletingContacts(false);
    }
  };

  const openContactHistory = async (contact) => {
    if (!contact?.id) return;
    setHistoryOpen(true);
    setHistoryContact(contact);
    setHistoryMessages([]);
    setHistoryMeta(null);
    setHistoryError('');
    setHistoryLoading(true);
    setExpandedMessageId(null);
    try {
      const res = await axios.get(`/api/contacts/${contact.id}/emails`, { timeout: 40000 });
      if (res.data?.success) {
        setHistoryContact(res.data.contact || contact);
        setHistoryMessages(res.data.messages || []);
        setHistoryMeta(res.data.meta || null);
        if (res.data.messages?.length) {
          setExpandedMessageId(res.data.messages[res.data.messages.length - 1].id);
        }
      } else {
        setHistoryError(res.data?.error || 'E-Mails konnten nicht geladen werden');
      }
    } catch (error) {
      setHistoryError(error.response?.data?.error || error.message || 'E-Mails konnten nicht geladen werden');
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeContactHistory = () => {
    setHistoryOpen(false);
    setHistoryContact(null);
    setHistoryMessages([]);
    setHistoryMeta(null);
    setHistoryError('');
    setExpandedMessageId(null);
  };

  const deleteHistorySentEmail = (msg) => {
    if (!msg || msg.direction !== 'outbound' || msg.source !== 'tracking') return;
    const match = String(msg.id || '').match(/^out-track-(\d+)$/);
    const trackingId = match ? Number(match[1]) : null;
    if (!trackingId) {
      toast.error('Diese E-Mail kann hier nicht gelöscht werden');
      return;
    }
    setHistoryEmailToDelete({ msg, trackingId });
  };

  const confirmDeleteHistorySentEmail = async () => {
    if (!historyEmailToDelete || deletingHistoryEmail) return;
    const { msg, trackingId } = historyEmailToDelete;
    setDeletingHistoryEmail(true);
    try {
      await axios.delete(`/api/sent-email/${trackingId}`);
      toast.success('Gesendete E-Mail gelöscht');
      setHistoryMessages((prev) => prev.filter((m) => m.id !== msg.id));
      setHistoryMeta((prev) => prev ? {
        ...prev,
        outbound: Math.max(0, (prev.outbound || 0) - 1),
      } : prev);
      if (expandedMessageId === msg.id) setExpandedMessageId(null);
      setHistoryEmailToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Löschen fehlgeschlagen');
    } finally {
      setDeletingHistoryEmail(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || anyCsvImporting) return;
    setImportingCsv(true);
    try {
      const parsed = await parseContactImportFile(file);
      if (parsed.length === 0) {
        toast.error('Keine gültigen Daten in der Datei gefunden');
        return;
      }
      setCsvImportMode('full');
      setCsvFileName(file.name || 'import');
      setCsvPreviewRows(parsed);
      setShowCsvImport(true);
    } catch (err) {
      toast.error(err?.message || 'Datei konnte nicht gelesen werden');
    } finally {
      setImportingCsv(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleWebsiteImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || anyCsvImporting) return;
    setImportingWebsiteCsv(true);
    try {
      const parsed = await parseWebsiteImportFile(file);
      if (parsed.length === 0) {
        toast.error('Keine Websites in der Datei gefunden');
        return;
      }
      setCsvImportMode('website');
      setCsvFileName(file.name || 'websites');
      setCsvPreviewRows(parsed);
      setShowCsvImport(true);
    } catch (err) {
      toast.error(err?.message || 'Datei konnte nicht gelesen werden');
    } finally {
      setImportingWebsiteCsv(false);
      if (websiteFileInputRef.current) websiteFileInputRef.current.value = '';
    }
  };

  const handleEmailWebsiteImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || anyCsvImporting) return;
    setImportingEmailWebsiteCsv(true);
    try {
      const parsed = await parseEmailWebsiteImportFile(file);
      if (parsed.length === 0) {
        toast.error('Keine E-Mail/Website-Daten in der Datei gefunden');
        return;
      }
      setCsvImportMode('emailWebsite');
      setCsvFileName(file.name || 'email-website');
      setCsvPreviewRows(parsed);
      setShowCsvImport(true);
    } catch (err) {
      toast.error(err?.message || 'Datei konnte nicht gelesen werden');
    } finally {
      setImportingEmailWebsiteCsv(false);
      if (emailWebsiteFileInputRef.current) emailWebsiteFileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      website: '',
      notes: '',
      gender: 'unknown',
      name2: '',
      gender2: 'unknown',
      categoryIds: [],
    });
    setEditingContact(null);
    setGenderManual(false);
    setGender2Manual(false);
    setShowModal(false);
    setShowFormNewCategory(false);
    setNewCategoryName('');
    setImpressumText('');
    setImpressumHtml('');
  };

  const handleNameChange = (value) => {
    setFormData((prev) => {
      const next = { ...prev, name: value };
      if (isCompanyAsContactName(value, next.company)) {
        next.gender = 'unknown';
        return next;
      }
      if (!genderManual) {
        next.gender = guessGenderFromName(value, contacts, {
          excludeId: editingContact?.id,
        });
      }
      return next;
    });
  };

  const handleCompanyChange = (value) => {
    setFormData((prev) => {
      const next = { ...prev, company: value };
      if (isCompanyAsContactName(next.name, value)) {
        next.gender = 'unknown';
      }
      return next;
    });
  };

  const handleGenderChange = (value) => {
    setGenderManual(true);
    setFormData((prev) => ({ ...prev, gender: value }));
  };

  const handleName2Change = (value) => {
    setFormData((prev) => {
      const next = { ...prev, name2: value };
      if (!String(value || '').trim()) {
        next.gender2 = 'unknown';
        return next;
      }
      if (!gender2Manual) {
        next.gender2 = guessGenderFromName(value, contacts, {
          excludeId: editingContact?.id,
        });
      }
      return next;
    });
  };

  const handleGender2Change = (value) => {
    setGender2Manual(true);
    setFormData((prev) => ({ ...prev, gender2: value }));
  };

  const normalizeUrl = (value) => {
    if (!value) return '';
    const t = String(value).trim();
    if (t.toLowerCase().startsWith('http://')) return 'https://' + t.slice(7);
    if (!/^https?:\/\//i.test(t)) return 'https://' + t;
    return t;
  };

  const scrapeImpressumIntoForm = async (websiteRaw, { preserveEmail = false } = {}) => {
    if (!websiteRaw?.trim()) {
      toast.error('Bitte zuerst eine Website angeben');
      return;
    }
    try {
      setScraping(true);
      const normalized = normalizeUrl(websiteRaw);
      const res = await axios.post('/api/scrape-impressum', { website: normalized });
      if (res.data?.fullText) setImpressumText(res.data.fullText);
      if (res.data?.fullHtml) setImpressumHtml(res.data.fullHtml);
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        setFormData((prev) => {
          const nextName = (d.name && String(d.name).trim()) || prev.name;
          const nextName2 = d.name2 != null ? String(d.name2).trim() : prev.name2;
          const next = {
            ...prev,
            website: normalized,
            name: nextName,
            name2: nextName2 || '',
            email: preserveEmail
              ? prev.email
              : ((d.email && String(d.email).trim()) || prev.email),
            company: (d.company && String(d.company).trim()) || prev.company,
            phone: (d.phone && String(d.phone).trim()) || prev.phone,
            notes: (d.notes && String(d.notes).trim()) || prev.notes,
          };
          if (!genderManual && d.name && String(d.name).trim()) {
            next.gender = isCompanyAsContactName(nextName, next.company)
              ? 'unknown'
              : guessGenderFromName(nextName, contacts, {
                  excludeId: editingContact?.id,
                });
          }
          if (nextName2) {
            if (!gender2Manual) {
              next.gender2 = guessGenderFromName(nextName2, contacts, {
                excludeId: editingContact?.id,
              });
            }
          } else {
            next.gender2 = 'unknown';
          }
          return next;
        });
        const filled = ['name', 'name2', 'email', 'company', 'phone'].filter(
          (k) => d[k] && String(d[k]).trim()
        );
        toast.success(
          filled.length
            ? `Übernommen: ${filled
                .map(
                  (k) =>
                    ({
                      name: 'Name',
                      name2: '2. Ansprechpartner',
                      email: 'E-Mail',
                      company: 'Unternehmen',
                      phone: 'Telefon',
                    })[k]
                )
                .join(', ')}`
            : 'Impressum geladen, aber keine Felder erkannt'
        );
      } else {
        toast.error(res.data?.error || 'Keine Daten gefunden');
      }
    } catch (e) {
      toast.error('Fehler beim Abrufen: ' + (e.response?.data?.error || e.message));
    } finally {
      setScraping(false);
    }
  };

  const handleGenerateFromWebsite = () => scrapeImpressumIntoForm(formData.website);

  const handleGenerateFromEmail = () => {
    const email = String(formData.email || '').trim();
    if (!email || !email.includes('@')) {
      toast.error('Bitte zuerst eine E-Mail angeben');
      return;
    }
    const domain = emailDomain(email);
    if (!domain) {
      toast.error('Ungültige E-Mail-Adresse');
      return;
    }
    if (detectMailProvider(email) || FREE_EMAIL_DOMAINS.has(domain)) {
      toast.error('Bei Freemail-Adressen (Gmail, GMX, …) keine Firmenwebsite ableitbar');
      return;
    }
    return scrapeImpressumIntoForm(domain, { preserveEmail: true });
  };

  const toggleFormCategory = (categoryId) => {
    const id = Number(categoryId);
    setFormData((prev) => {
      const current = Array.isArray(prev.categoryIds) ? prev.categoryIds.map(Number) : [];
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      return { ...prev, categoryIds: next };
    });
  };

  const handleCreateCategory = async () => {
    const name = String(newCategoryName || '').trim();
    if (!name) {
      toast.error('Name für die Kategorie fehlt');
      return;
    }
    setCategoryManagerBusy(true);
    try {
      const res = await axios.post('/api/contact-categories', {
        name,
        color: newCategoryColor,
      });
      const created = res.data?.category;
      if (created) {
        setCategories((prev) =>
          [...prev, created].sort((a, b) =>
            String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' })
          )
        );
        setFormData((prev) => ({
          ...prev,
          categoryIds: [...(prev.categoryIds || []), created.id],
        }));
      }
      setNewCategoryName('');
      setNewCategoryColor(CATEGORY_COLOR_PRESETS[(categories.length + 1) % CATEGORY_COLOR_PRESETS.length]);
      setShowFormNewCategory(false);
      toast.success('Kategorie erstellt');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Kategorie konnte nicht erstellt werden');
    } finally {
      setCategoryManagerBusy(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!category?.id) return;
    if (!window.confirm(`Kategorie „${category.name}“ wirklich löschen?`)) return;
    setCategoryManagerBusy(true);
    try {
      await axios.delete(`/api/contact-categories/${category.id}`);
      setCategories((prev) => prev.filter((c) => c.id !== category.id));
      setFormData((prev) => ({
        ...prev,
        categoryIds: (prev.categoryIds || []).filter((id) => Number(id) !== Number(category.id)),
      }));
      if (editingCategoryId === category.id) {
        setEditingCategoryId(null);
        setEditCategoryName('');
      }
      if (String(filterCategory) === String(category.id)) setFilterCategory('');
      await fetchContacts({ quiet: true });
      toast.success('Kategorie gelöscht');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Löschen fehlgeschlagen');
    } finally {
      setCategoryManagerBusy(false);
    }
  };

  const startEditCategory = (category) => {
    if (!category?.id || categoryManagerBusy) return;
    setEditingCategoryId(category.id);
    setEditCategoryName(category.name || '');
    setEditCategoryColor(category.color || CATEGORY_COLOR_PRESETS[0]);
  };

  const cancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditCategoryName('');
    setEditCategoryColor(CATEGORY_COLOR_PRESETS[0]);
  };

  const handleSaveCategory = async () => {
    const id = Number(editingCategoryId);
    const name = String(editCategoryName || '').trim();
    if (!id) return;
    if (!name) {
      toast.error('Name für die Kategorie fehlt');
      return;
    }
    setCategoryManagerBusy(true);
    try {
      const res = await axios.put(`/api/contact-categories/${id}`, {
        name,
        color: editCategoryColor,
      });
      const updated = res.data?.category;
      if (updated) {
        setCategories((prev) =>
          prev
            .map((c) => (Number(c.id) === id ? { ...c, ...updated } : c))
            .sort((a, b) =>
              String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' })
            )
        );
        await fetchContacts({ quiet: true });
        notifyContactsChanged();
      }
      cancelEditCategory();
      toast.success('Kategorie gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setCategoryManagerBusy(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        for (const c of paginatedContacts) next.delete(c.id);
      } else {
        for (const c of paginatedContacts) next.add(c.id);
      }
      return next;
    });
  };

  const renderContactsPagination = () => {
    const showPager = filteredContacts.length > CONTACTS_PAGE_SIZE;
    if (!showPager && !loadingMoreContacts) return null;
    const from = (contactsPageSafe - 1) * CONTACTS_PAGE_SIZE + 1;
    const to = Math.min(contactsPageSafe * CONTACTS_PAGE_SIZE, filteredContacts.length);
    return (
      <div className="flex flex-col gap-2 pt-1">
        {loadingMoreContacts ? (
          <p className="inline-flex items-center gap-1.5 text-[12px] text-neutral-500 dark:text-neutral-400 tabular-nums">
            <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" aria-hidden />
            Weitere Kontakte laden… {contacts.length}
            {contactsTotal > contacts.length ? `/${contactsTotal}` : ''}
          </p>
        ) : null}
        {showPager ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-[12px] text-neutral-500 dark:text-neutral-400 tabular-nums">
              {from}–{to} von {filteredContacts.length}
              {loadingMoreContacts && contactsTotal > filteredContacts.length
                ? ` · gesamt ~${contactsTotal}`
                : ''}
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setContactsPage((p) => Math.max(1, p - 1))}
                disabled={contactsPageSafe <= 1}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:pointer-events-none"
                aria-label="Vorherige Seite"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="min-w-[4.5rem] text-center text-[12px] font-medium text-neutral-700 dark:text-neutral-200 tabular-nums">
                {contactsPageSafe} / {contactsTotalPages}
              </span>
              <button
                type="button"
                onClick={() => setContactsPage((p) => Math.min(contactsTotalPages, p + 1))}
                disabled={contactsPageSafe >= contactsTotalPages}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:pointer-events-none"
                aria-label="Nächste Seite"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  if (loading) {
    return <PageSkeleton variant="contacts" />;
  }

  return (
    <div className="space-y-4 sm:space-y-6 pb-20 sm:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center sm:gap-4 fade-in">
        <div className="min-w-0">
          <h1 className="page-title">Kontakte</h1>
          <p className="page-subtitle">
            {loadingMoreContacts ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-neutral-400" aria-hidden />
                {contacts.length}
                {contactsTotal > contacts.length ? `/${contactsTotal}` : ''} Kontakte laden…
              </span>
            ) : (
              <>
                {contactsTotal > 0 ? contactsTotal : contacts.length} Kontakte
              </>
            )}
            <span className="text-neutral-400"> · {companies.length} Unternehmen</span>
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
          onChange={handleImport}
          className="hidden"
        />
        <input
          ref={websiteFileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
          onChange={handleWebsiteImport}
          className="hidden"
        />
        <input
          ref={emailWebsiteFileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
          onChange={handleEmailWebsiteImport}
          className="hidden"
        />
        {/* Mobile: Primär-CTA + Overflow */}
        <div className="flex items-center gap-2 sm:hidden">
          <button
            type="button"
            onClick={() => {
              setGenderManual(false);
              setShowModal(true);
            }}
            className="btn-primary flex-1 justify-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neu
          </button>
          <button
            ref={mobileActionsBtnRef}
            type="button"
            onClick={() => {
              setMobileActionsOpen((open) => {
                if (open) {
                  setMobileActionsPos(null);
                  return false;
                }
                const el = mobileActionsBtnRef.current;
                if (el) {
                  const rect = el.getBoundingClientRect();
                  setMobileActionsPos({
                    top: Math.round(rect.bottom + 6),
                    right: Math.round(Math.max(8, window.innerWidth - rect.right)),
                  });
                }
                return true;
              });
            }}
            className="btn-secondary !px-3 relative z-10"
            aria-label="Weitere Aktionen"
            aria-expanded={mobileActionsOpen}
            aria-haspopup="menu"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {mobileActionsOpen && mobileActionsPos
            ? createPortal(
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-[250] cursor-default bg-transparent"
                    aria-label="Menü schließen"
                    onClick={() => setMobileActionsOpen(false)}
                  />
                  <div
                    role="menu"
                    className="fixed z-[260] min-w-[11.5rem] rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl py-1"
                    style={{ top: mobileActionsPos.top, right: mobileActionsPos.right }}
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                      onClick={() => {
                        setMobileActionsOpen(false);
                        setShowImageImport(true);
                      }}
                    >
                      <ScanSearch className="w-4 h-4 text-neutral-400" />
                      Bild-Import
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      disabled={anyCsvImporting}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                      onClick={() => {
                        setMobileActionsOpen(false);
                        fileInputRef.current?.click();
                      }}
                    >
                      {importingCsv ? (
                        <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
                      ) : (
                        <Upload className="w-4 h-4 text-neutral-400" />
                      )}
                      {importingCsv ? 'Wird gelesen…' : 'Import'}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      disabled={anyCsvImporting}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                      onClick={() => {
                        setMobileActionsOpen(false);
                        emailWebsiteFileInputRef.current?.click();
                      }}
                    >
                      {importingEmailWebsiteCsv ? (
                        <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
                      ) : (
                        <Mail className="w-4 h-4 text-neutral-400" />
                      )}
                      {importingEmailWebsiteCsv ? 'Wird gelesen…' : 'E-Mail + Website'}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      disabled={anyCsvImporting}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                      onClick={() => {
                        setMobileActionsOpen(false);
                        websiteFileInputRef.current?.click();
                      }}
                    >
                      {importingWebsiteCsv ? (
                        <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
                      ) : (
                        <Globe className="w-4 h-4 text-neutral-400" />
                      )}
                      {importingWebsiteCsv ? 'Wird gelesen…' : 'Website-Import'}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      disabled={filteredContacts.length === 0}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                      onClick={() => {
                        setMobileActionsOpen(false);
                        exportContactsToCSV(filteredContacts);
                      }}
                    >
                      <Download className="w-4 h-4 text-neutral-400" />
                      Export
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                      onClick={() => {
                        setMobileActionsOpen(false);
                        setShowCategoryManager(true);
                      }}
                    >
                      <Tag className="w-4 h-4 text-neutral-400" />
                      Kategorien
                    </button>
                  </div>
                </>,
                document.body
              )
            : null}
        </div>
        {/* Desktop actions */}
        <div className="hidden sm:flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => setShowImageImport(true)} className="btn-secondary">
            <ScanSearch className="w-4 h-4 mr-2" />
            Bild-Import
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary"
            disabled={anyCsvImporting}
            aria-busy={importingCsv}
          >
            {importingCsv ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {importingCsv ? 'Wird gelesen…' : 'Import'}
          </button>
          <button
            type="button"
            onClick={() => emailWebsiteFileInputRef.current?.click()}
            className="btn-secondary"
            disabled={anyCsvImporting}
            aria-busy={importingEmailWebsiteCsv}
            title="CSV nur mit E-Mail und Website"
          >
            {importingEmailWebsiteCsv ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Mail className="w-4 h-4 mr-2" />
            )}
            {importingEmailWebsiteCsv ? 'Wird gelesen…' : 'E-Mail + Website'}
          </button>
          <button
            type="button"
            onClick={() => websiteFileInputRef.current?.click()}
            className="btn-secondary"
            disabled={anyCsvImporting}
            aria-busy={importingWebsiteCsv}
            title="CSV mit Websites — Kontaktdaten werden aus dem Impressum geladen"
          >
            {importingWebsiteCsv ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Globe className="w-4 h-4 mr-2" />
            )}
            {importingWebsiteCsv ? 'Wird gelesen…' : 'Website-Import'}
          </button>
          <button
            type="button"
            onClick={() => exportContactsToCSV(filteredContacts)}
            className="btn-secondary"
            disabled={filteredContacts.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button
            type="button"
            onClick={() => setShowCategoryManager(true)}
            className="btn-secondary"
            title="Kategorien verwalten"
          >
            <Tag className="w-4 h-4 mr-2" />
            Kategorien
          </button>
          <button
            type="button"
            onClick={() => {
              setGenderManual(false);
              setShowModal(true);
            }}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neuer Kontakt
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="fade-in space-y-2.5 sm:space-y-0 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
        <div className="relative w-full min-w-0 sm:flex-1 sm:min-w-[13.8rem] sm:max-w-[32.2rem]">
          <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-neutral-400 w-4 h-4 sm:w-5 sm:h-5" />
          <input
            type="text"
            placeholder="Name, E-Mail, Telefon…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-9 sm:pl-11 w-full"
          />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:contents">
          <div className="relative min-w-0">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 w-4 h-4 pointer-events-none z-10" />
            <SelectField
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="input-field pl-9 w-full sm:min-w-[10.5rem]"
              wrapperClassName="w-full sm:w-auto"
              title="Nach Kategorie filtern"
            >
              <option value="">Alle Kategorien</option>
              <option value="__none__">Ohne Kategorie</option>
              {categories.map((cat) => (
                <option key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </option>
              ))}
            </SelectField>
          </div>
          <div className="relative min-w-0">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 w-4 h-4 pointer-events-none z-10" />
            <SelectField
              value={missingFilter}
              onChange={(e) => setMissingFilter(e.target.value)}
              className="input-field pl-9 w-full sm:min-w-[10.5rem]"
              wrapperClassName="w-full sm:w-auto"
              title="Nur Kontakte mit fehlenden Daten"
            >
              <option value="">Alle Datenstände</option>
              <option value="any">Daten unvollständig</option>
              <option value="company">Ohne Unternehmen</option>
              <option value="phone">Ohne Telefon</option>
              <option value="website">Ohne Website</option>
              <option value="gender">Ohne Geschlecht</option>
            </SelectField>
          </div>
          <div className="relative min-w-0 col-span-2 sm:col-span-1">
            <ArrowDownWideNarrow className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 w-4 h-4 pointer-events-none z-10" />
            <SelectField
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-field pl-9 w-full sm:min-w-[10.5rem]"
              wrapperClassName="w-full sm:w-auto"
              title="Sortierung"
            >
              <option value="name-asc">Name A–Z</option>
              <option value="name-desc">Name Z–A</option>
              <option value="company-asc">Unternehmen A–Z</option>
              <option value="email-asc">E-Mail A–Z</option>
              <option value="newest">Neueste zuerst</option>
            </SelectField>
          </div>
        </div>
      </div>

      {/* Bulk Actions — kompakt auf Mobile */}
      {selectedIds.size > 0 && createPortal(
        <div className="fixed inset-x-0 bottom-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:bottom-5 z-[90] flex justify-center px-3 pointer-events-none">
          <div className="pointer-events-auto inline-flex w-fit items-center gap-1.5 sm:gap-2 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white/95 dark:bg-neutral-900/95 shadow-lg backdrop-blur-sm px-2 py-1.5 sm:px-3 sm:py-2 fade-in">
            <span className="pl-1.5 pr-0.5 text-xs sm:text-[13px] font-medium text-neutral-700 dark:text-neutral-200 tabular-nums whitespace-nowrap">
              {selectedIds.size}
              <span className="font-normal text-neutral-500 dark:text-neutral-400"> ausgewählt</span>
            </span>
            <span className="w-px h-4 bg-neutral-200 dark:bg-neutral-700 flex-shrink-0" />
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <button
                type="button"
                onClick={() => exportContactsToCSV(contacts.filter((c) => selectedIds.has(c.id)))}
                className="inline-flex items-center justify-center gap-1 rounded-full min-h-9 min-w-9 sm:min-h-0 sm:min-w-0 sm:px-2.5 sm:py-1.5 text-xs font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 touch-manipulation"
                title="Auswahl exportieren"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                className="inline-flex items-center justify-center gap-1 rounded-full min-h-9 min-w-9 sm:min-h-0 sm:min-w-0 sm:px-2.5 sm:py-1.5 text-xs font-medium text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/30 touch-manipulation"
                title="Auswahl löschen"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Löschen</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="inline-flex items-center justify-center rounded-full min-h-9 min-w-9 sm:min-h-0 sm:min-w-0 sm:p-1.5 text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 touch-manipulation"
                title="Auswahl aufheben"
                aria-label="Abbrechen"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="fade-in space-y-3">
          {/* Mobile: gestapelte Zeilen ohne Horizontal-Scroll */}
          <div className="md:hidden rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden bg-white dark:bg-neutral-900 divide-y divide-neutral-100 dark:divide-neutral-800">
            <div className="flex items-center gap-3 px-3 py-2.5 bg-neutral-50/80 dark:bg-neutral-950/40">
              <input
                type="checkbox"
                checked={allPageSelected}
                onChange={toggleSelectAll}
                className="h-4 w-4 rounded text-primary-600"
                aria-label="Seite auswählen"
              />
              <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wide">
                {filteredContacts.length} Kontakt{filteredContacts.length === 1 ? '' : 'e'}
              </span>
            </div>
            {paginatedContacts.map((c) => {
              const optedOut = isContactUnsubscribed(c);
              return (
                <div
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openContactDetail(c)}
                  onContextMenu={(e) => openContextMenu(e, c)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openContactDetail(c);
                    }
                  }}
                  className={`flex items-start gap-2.5 px-3 py-2.5 cursor-pointer transition-colors ${
                    optedOut
                      ? 'bg-red-50 dark:bg-red-950/30'
                      : selectedIds.has(c.id)
                        ? 'bg-primary-50 dark:bg-primary-950/40'
                        : 'active:bg-neutral-50 dark:active:bg-neutral-800/60'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(c.id)}
                    onChange={() => toggleSelect(c.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1.5 h-4 w-4 flex-shrink-0 rounded text-primary-600"
                  />
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${
                      optedOut
                        ? 'bg-red-200 dark:bg-red-900/60 text-red-800 dark:text-red-200'
                        : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300'
                    }`}
                  >
                    {getInitials(c.name || c.email)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span
                        className={`text-sm font-medium truncate ${
                          optedOut
                            ? 'text-red-800 dark:text-red-200'
                            : 'text-neutral-900 dark:text-white'
                        }`}
                      >
                        {c.name2 ? `${c.name} & ${c.name2}` : c.name || 'Ohne Name'}
                      </span>
                      {optedOut ? (
                        <span className="flex-shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-red-600 text-white">
                          Ab
                        </span>
                      ) : isContactBounced(c) ? (
                        <span className="flex-shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-amber-600 text-white">
                          Bounce
                        </span>
                      ) : null}
                    </div>
                    <p
                      className={`text-[12px] truncate ${
                        optedOut
                          ? 'text-red-700 dark:text-red-300'
                          : 'text-neutral-600 dark:text-neutral-300'
                      }`}
                    >
                      {c.email}
                    </p>
                    {(c.company || c.phone) ? (
                      <p className="text-[11px] text-neutral-500 truncate">
                        {[c.company, c.phone].filter(Boolean).join(' · ')}
                      </p>
                    ) : null}
                    <ContactCategoryChips categories={c.categories} className="mt-1" max={2} />
                  </div>
                  <div
                    className="flex flex-col gap-0.5 flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => openContactHistory(c)}
                      className="p-1.5 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                      title="E-Mail-Verlauf"
                      aria-label="E-Mail-Verlauf"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                    {c.website ? (
                      <a
                        href={normalizeUrl(c.website)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-1.5 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                        title="Website öffnen"
                        aria-label="Website öffnen"
                      >
                        <Globe className="w-4 h-4" />
                      </a>
                    ) : null}
                    <button
                      type="button"
                      onClick={(e) => handleEdit(c, e)}
                      className="p-1.5 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                      title="Bearbeiten"
                      aria-label="Bearbeiten"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: Tabelle */}
          <div className="hidden md:block card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-100 dark:border-neutral-800">
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded text-primary-600"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                      E-Mail
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                      Unternehmen
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider hidden lg:table-cell">
                      Telefon
                    </th>
                    <th className="px-4 py-3 text-right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {paginatedContacts.map((c) => {
                    const optedOut = isContactUnsubscribed(c);
                    return (
                      <tr
                        key={c.id}
                        className={`transition-colors cursor-pointer ${
                          optedOut
                            ? 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100/80 dark:hover:bg-red-950/45'
                            : selectedIds.has(c.id)
                              ? 'bg-primary-50 dark:bg-primary-950/40 hover:bg-primary-100/80 dark:hover:bg-primary-950/55'
                              : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
                        }`}
                        onClick={() => openContactDetail(c)}
                        onContextMenu={(e) => openContextMenu(e, c)}
                      >
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(c.id)}
                            onChange={() => toggleSelect(c.id)}
                            className="h-4 w-4 rounded text-primary-600"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${
                                optedOut
                                  ? 'bg-red-200 dark:bg-red-900/60 text-red-800 dark:text-red-200'
                                  : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300'
                              }`}
                            >
                              {getInitials(c.name || c.email)}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span
                                  className={`text-sm font-medium truncate ${
                                    optedOut
                                      ? 'text-red-800 dark:text-red-200'
                                      : 'text-neutral-900 dark:text-white'
                                  }`}
                                >
                                  {c.name2 ? `${c.name} & ${c.name2}` : c.name}
                                </span>
                                {optedOut ? (
                                  <span className="flex-shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-red-600 text-white">
                                    Abgemeldet
                                  </span>
                                ) : isContactBounced(c) ? (
                                  <span className="flex-shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-amber-600 text-white">
                                    Unzustellbar
                                  </span>
                                ) : null}
                              </div>
                              {optedOut && c.unsubscribed_at ? (
                                <p className="text-[10px] text-red-600 dark:text-red-400">
                                  seit {formatUnsubscribedAt(c.unsubscribed_at)}
                                </p>
                              ) : null}
                              <ContactCategoryChips categories={c.categories} className="mt-0.5" max={2} />
                            </div>
                          </div>
                        </td>
                        <td
                          className={`px-4 py-3 text-sm max-w-[14rem] truncate ${
                            optedOut
                              ? 'text-red-700 dark:text-red-300'
                              : 'text-neutral-600 dark:text-neutral-300'
                          }`}
                          title={c.email}
                        >
                          {c.email}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-500 max-w-[10rem] truncate">
                          {c.company || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-500 hidden lg:table-cell">
                          {c.phone || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            {c.website ? (
                              <a
                                href={normalizeUrl(c.website)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                                title="Website öffnen"
                              >
                                <Globe className="w-4 h-4" />
                              </a>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => openContactHistory(c)}
                              className="p-2 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                              title="E-Mail-Verlauf"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleEdit(c, e)}
                              className="p-2 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleDelete(c.id, e)}
                              className="p-2 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {renderContactsPagination()}
        </div>

      {filteredContacts.length === 0 && (
        <div className="text-center py-16 fade-in">
          <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-800 rounded-2xl flex items-center justify-center mx-auto mb-4"><User className="w-8 h-8 text-neutral-300 dark:text-neutral-600" /></div>
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">{hasActiveFilters ? 'Keine Kontakte gefunden' : 'Noch keine Kontakte'}</h3>
          <p className="text-neutral-500 mb-6">{hasActiveFilters ? 'Versuchen Sie andere Filter oder Sortierung' : 'Erstellen Sie Ihren ersten Kontakt oder importieren Sie CSV / Excel'}</p>
          {!hasActiveFilters && (
            <div className="flex justify-center gap-3 flex-wrap">
              <button onClick={() => { setGenderManual(false); setShowModal(true); }} className="btn-primary"><Plus className="w-4 h-4 mr-2" />Ersten Kontakt</button>
              <button onClick={() => setShowImageImport(true)} className="btn-secondary"><ScanSearch className="w-4 h-4 mr-2" />Bild-Import</button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="btn-secondary"
                disabled={anyCsvImporting}
                aria-busy={importingCsv}
              >
                {importingCsv ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {importingCsv ? 'Wird gelesen…' : 'Import'}
              </button>
              <button
                type="button"
                onClick={() => emailWebsiteFileInputRef.current?.click()}
                className="btn-secondary"
                disabled={anyCsvImporting}
                aria-busy={importingEmailWebsiteCsv}
              >
                {importingEmailWebsiteCsv ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                {importingEmailWebsiteCsv ? 'Wird gelesen…' : 'E-Mail + Website'}
              </button>
              <button
                type="button"
                onClick={() => websiteFileInputRef.current?.click()}
                className="btn-secondary"
                disabled={anyCsvImporting}
                aria-busy={importingWebsiteCsv}
              >
                {importingWebsiteCsv ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Globe className="w-4 h-4 mr-2" />
                )}
                {importingWebsiteCsv ? 'Wird gelesen…' : 'Website-Import'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Kontakt-Details Modal */}
      {detailContact && createPortal(
        <ContactDetailModal
          contact={detailContact}
          onClose={closeContactDetail}
          onUpdated={(updated) => {
            const next = withNormalizedSubscription({
              ...updated,
              id: Number(updated.id),
            });
            setDetailContact(next);
            setContacts((list) =>
              list.map((c) => (Number(c.id) === Number(next.id) ? { ...c, ...next } : c))
            );
          }}
          onDelete={(c) => {
            closeContactDetail();
            handleDelete(c.id);
          }}
          onHistory={(c) => {
            closeContactDetail();
            openContactHistory(c);
          }}
          onEdit={(c) => {
            closeContactDetail();
            handleEdit(c);
          }}
        />,
        document.body
      )}

      {/* Edit/Create Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={resetForm} />
          <div className="relative bg-white dark:bg-neutral-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-[37rem] max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
              <div className="flex-shrink-0 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-neutral-100 dark:border-neutral-800">
                <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-neutral-200 dark:bg-neutral-700 sm:hidden" />
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">{editingContact ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}</h3>
                  <button type="button" onClick={resetForm} className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4">
                <div className="space-y-2.5">
                  <div className="space-y-2">
                    <div className={`grid gap-2 items-end ${String(formData.name || '').trim() ? 'sm:grid-cols-[minmax(0,1fr)_11.5rem]' : 'grid-cols-1'}`}>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                          Ansprechpartner *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleNameChange(e.target.value)}
                          className="input-field"
                          required
                          placeholder="z. B. Max Mustermann"
                        />
                      </div>
                      {String(formData.name || '').trim() ? (
                        <div className="min-w-0">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            Geschlecht
                          </label>
                          <SelectField
                            value={formData.gender || 'unknown'}
                            onChange={(e) => handleGenderChange(e.target.value)}
                            className="input-field"
                          >
                            <option value="unknown">Keine Angabe</option>
                            <option value="male">Männlich</option>
                            <option value="female">Weiblich</option>
                          </SelectField>
                        </div>
                      ) : null}
                    </div>
                    <div className={`grid gap-2 items-end ${String(formData.name2 || '').trim() ? 'sm:grid-cols-[minmax(0,1fr)_11.5rem]' : 'grid-cols-1'}`}>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                          2. Ansprechpartner{' '}
                          <span className="font-normal text-neutral-400">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name2 || ''}
                          onChange={(e) => handleName2Change(e.target.value)}
                          className="input-field"
                          placeholder="z. B. Erika Musterfrau"
                        />
                      </div>
                      {String(formData.name2 || '').trim() ? (
                        <div className="min-w-0">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            Geschlecht
                          </label>
                          <SelectField
                            value={formData.gender2 || 'unknown'}
                            onChange={(e) => handleGender2Change(e.target.value)}
                            className="input-field"
                          >
                            <option value="unknown">Keine Angabe</option>
                            <option value="male">Männlich</option>
                            <option value="female">Weiblich</option>
                          </SelectField>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {(impressumText || impressumHtml) && (
                    <div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
                        <span className="text-sm text-neutral-600 dark:text-neutral-400">Gefundener Impressum-Inhalt</span>
                        <div className="flex flex-wrap items-center gap-2">
                          <button type="button" onClick={() => setShowFullText((v) => !v)} className="px-2 py-1 text-xs rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors">{showFullText ? 'Verbergen' : 'Text'}</button>
                          {impressumHtml && <button type="button" onClick={() => setShowHtml((v) => !v)} className="px-2 py-1 text-xs rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors">{showHtml ? 'Verbergen' : 'HTML'}</button>}
                          <button type="button" onClick={() => { navigator.clipboard.writeText(impressumText); toast.success('Kopiert'); }} className="px-2 py-1 text-xs rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800">Kopieren</button>
                        </div>
                      </div>
                      {showFullText && impressumText && <textarea readOnly value={impressumText} className="w-full h-40 text-xs p-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300" />}
                      {showHtml && impressumHtml && <div className="w-full max-h-64 overflow-auto text-xs p-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(impressumHtml, { USE_PROFILES: { html: true }, FORBID_TAGS: ['img', 'svg', 'object', 'iframe'], FORBID_ATTR: ['srcset', 'style'] }) }} />}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">E-Mail *</label>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="input-field flex-1 min-w-0"
                        required
                      />
                      <button
                        type="button"
                        onClick={handleGenerateFromEmail}
                        disabled={scraping}
                        className="px-3 py-2.5 sm:py-2 text-xs rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-1 flex-shrink-0 w-full sm:w-auto"
                      >
                        {scraping ? <><div className="spinner-sm"></div>Lädt…</> : <><Sparkles className="w-3 h-3" />Generieren</>}
                      </button>
                    </div>
                  </div>
                  <div><label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">Unternehmen</label><input type="text" value={formData.company} onChange={(e) => handleCompanyChange(e.target.value)} className="input-field" /></div>
                  <div><label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">Telefon</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="input-field" placeholder="+49 123 456 789" /></div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">Website</label>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <input
                        type="text"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        className="input-field flex-1 min-w-0"
                        placeholder="example.com"
                      />
                      <button
                        type="button"
                        onClick={handleGenerateFromWebsite}
                        disabled={scraping}
                        className="px-3 py-2.5 sm:py-2 text-xs rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-1 flex-shrink-0 w-full sm:w-auto"
                      >
                        {scraping ? <><div className="spinner-sm"></div>Lädt…</> : <><Sparkles className="w-3 h-3" />Generieren</>}
                      </button>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                        Kategorien
                      </label>
                      <button
                        type="button"
                        disabled={categoryManagerBusy}
                        onClick={() => setShowFormNewCategory((v) => !v)}
                        className="inline-flex items-center gap-0.5 text-[11px] font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 disabled:opacity-50"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Neu
                      </button>
                    </div>
                    {categories.length === 0 && !showFormNewCategory ? (
                      <p className="text-xs text-neutral-500">
                        Noch keine Kategorien — mit „Neu“ anlegen.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {categories.map((cat) => {
                          const active = (formData.categoryIds || []).map(Number).includes(Number(cat.id));
                          return (
                            <button
                              key={cat.id}
                              type="button"
                              disabled={categoryManagerBusy}
                              onClick={() => toggleFormCategory(cat.id)}
                              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border transition-colors disabled:opacity-50 ${
                                active
                                  ? 'border-transparent text-white'
                                  : 'border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                              }`}
                              style={
                                active
                                  ? { backgroundColor: cat.color || '#0F766E' }
                                  : undefined
                              }
                            >
                              <span
                                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                  active ? 'border border-black' : ''
                                }`}
                                style={{ backgroundColor: cat.color || '#0F766E' }}
                              />
                              {cat.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {showFormNewCategory ? (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <input
                          type="text"
                          value={newCategoryName}
                          disabled={categoryManagerBusy}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleCreateCategory();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              setShowFormNewCategory(false);
                              setNewCategoryName('');
                            }
                          }}
                          className="input-field !py-1.5 !px-2.5 text-xs w-full sm:w-40 min-w-0"
                          placeholder="Neue Kategorie"
                          autoFocus
                        />
                        <div className="flex items-center gap-1">
                          {CATEGORY_COLOR_PRESETS.slice(0, 6).map((color) => (
                            <button
                              key={color}
                              type="button"
                              disabled={categoryManagerBusy}
                              onClick={() => setNewCategoryColor(color)}
                              className={`w-3.5 h-3.5 rounded-full border ${
                                newCategoryColor === color
                                  ? 'border-black'
                                  : 'border-transparent'
                              }`}
                              style={{ backgroundColor: color }}
                              aria-label={`Farbe ${color}`}
                            />
                          ))}
                        </div>
                        <button
                          type="button"
                          disabled={categoryManagerBusy || !String(newCategoryName || '').trim()}
                          onClick={handleCreateCategory}
                          className="text-[11px] font-medium text-primary-600 disabled:opacity-50"
                        >
                          Anlegen
                        </button>
                        <button
                          type="button"
                          disabled={categoryManagerBusy}
                          onClick={() => {
                            setShowFormNewCategory(false);
                            setNewCategoryName('');
                          }}
                          className="text-[11px] text-neutral-400"
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <div><label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">Notizen</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="3" className="input-field resize-none" /></div>
                </div>
              </div>
              <div className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-t border-neutral-100 dark:border-neutral-800 flex flex-col-reverse sm:flex-row-reverse gap-2 sm:gap-3 bg-white dark:bg-neutral-900 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-4">
                <button type="submit" className="btn-primary w-full sm:w-auto justify-center">{editingContact ? 'Aktualisieren' : 'Erstellen'}</button>
                <button type="button" onClick={resetForm} className="btn-secondary w-full sm:w-auto justify-center">Abbrechen</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* E-Mail-Verlauf Modal */}
      {historyOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-neutral-950/60 backdrop-blur-sm" onClick={closeContactHistory} />
          <div className="relative bg-white dark:bg-neutral-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden border border-neutral-200 dark:border-neutral-800">
            <div className="flex-shrink-0 px-4 sm:px-5 pt-3 sm:pt-4 pb-3 sm:pb-4 border-b border-neutral-200 dark:border-neutral-800">
              <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-neutral-200 dark:bg-neutral-700 sm:hidden" />
              <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                  {getInitials(historyContact?.name || historyContact?.email)}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-primary-600 dark:text-primary-400 mb-0.5">E-Mail-Verlauf</p>
                  <h2 className="text-lg font-semibold text-neutral-900 dark:text-white truncate">
                    {historyContact?.name || 'Kontakt'}
                  </h2>
                  <p className="text-xs text-neutral-500 truncate">
                    {historyContact?.email || ''}
                    {historyContact?.company ? ` · ${historyContact.company}` : ''}
                  </p>
                  {historyMeta && !historyLoading && (
                    <p className="text-[11px] text-neutral-400 mt-1">
                      {historyMeta.outbound || 0} gesendet · {historyMeta.inbound || 0} empfangen
                      {historyMeta.imapError ? ' · IMAP eingeschränkt' : ''}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {historyContact && (
                  <button
                    type="button"
                    onClick={() => { closeContactHistory(); handleEdit(historyContact); }}
                    className="btn-ghost p-1.5"
                    title="Kontakt bearbeiten"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                )}
                <button type="button" onClick={closeContactHistory} className="btn-ghost p-1.5" aria-label="Schließen">
                  <X className="w-5 h-5" />
                </button>
              </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0 p-4 sm:p-5 space-y-3">
              {historyLoading ? (
                <ModalSkeleton lines={4} />
              ) : historyError ? (
                <div className="rounded-xl border border-danger-200 dark:border-danger-900/50 bg-danger-50 dark:bg-danger-900/20 px-4 py-3 text-sm text-danger-700 dark:text-danger-300">
                  {historyError}
                </div>
              ) : historyMessages.length > 0 ? (
                <AnimatePresence initial={false}>
                  {historyMessages.map((msg) => {
                    const isOut = msg.direction === 'outbound';
                    const isOpen = expandedMessageId === msg.id;
                    const bodyHtml = msg.html
                      ? DOMPurify.sanitize(stripOpenTrackingPixels(msg.html), { USE_PROFILES: { html: true } })
                      : plainTextToHtml(msg.text);
                    return (
                      <motion.div
                        key={msg.id}
                        layout
                        {...historyItemMotion}
                        className={`rounded-2xl border overflow-hidden ${
                          isOut
                            ? 'border-neutral-200 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-800/40'
                            : 'border-success-200 dark:border-success-900/40 bg-success-50/40 dark:bg-success-900/10'
                        }`}
                      >
                        <div className="px-4 py-3 flex items-start gap-3">
                          <button
                            type="button"
                            onClick={() => setExpandedMessageId(isOpen ? null : msg.id)}
                            className="min-w-0 flex-1 text-left flex items-start gap-3"
                          >
                            <span
                              className={`mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                                isOut
                                  ? 'bg-neutral-200/80 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200'
                                  : 'bg-success-100 dark:bg-success-900/40 text-success-700 dark:text-success-300'
                              }`}
                            >
                              {isOut ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}
                              {isOut ? 'Gesendet' : 'Empfangen'}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                                {msg.subject || '(Kein Betreff)'}
                              </p>
                              <p className="text-[11px] text-neutral-500 truncate mt-0.5">
                                {formatDateLong(msg.date)}
                                {msg.templateName ? ` · ${msg.templateName}` : ''}
                              </p>
                            </div>
                          </button>
                          {isOut && msg.source === 'tracking' ? (
                            <button
                              type="button"
                              title="Löschen"
                              onClick={() => deleteHistorySentEmail(msg)}
                              className="p-1.5 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          ) : null}
                        </div>
                        {isOpen && (
                          <div className="px-4 pb-4 border-t border-neutral-200/70 dark:border-neutral-700/70 pt-3">
                            <div className="flex items-center justify-between gap-2 mb-3">
                              <p className="text-[11px] text-neutral-500 truncate min-w-0">
                                Von: {msg.from || '—'} · An: {msg.to || '—'}
                              </p>
                              {isDarkMode && bodyHtml && (
                                <button
                                  type="button"
                                  onClick={toggleEmailInvert}
                                  className={`inline-flex items-center rounded-lg border text-[11px] font-medium px-2 py-0.5 flex-shrink-0 ${
                                    invertEmailInDark
                                      ? 'border-neutral-900 bg-neutral-100 text-neutral-900 dark:border-neutral-300 dark:bg-neutral-800 dark:text-white'
                                      : 'border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300'
                                  }`}
                                  title="Helle E-Mail-Inhalte für Dark Mode invertieren"
                                >
                                  <Contrast className="w-3 h-3 mr-1" />
                                  {invertEmailInDark ? 'Original' : 'Invertieren'}
                                </button>
                              )}
                            </div>
                            {bodyHtml ? (
                              <div
                                className={emailHtmlClassName}
                                dangerouslySetInnerHTML={{ __html: bodyHtml }}
                              />
                            ) : (
                              <p className="text-sm text-neutral-400 italic">Kein Inhalt verfügbar</p>
                            )}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              ) : (
                <div className="text-center py-16">
                  <Mail className="w-10 h-10 text-neutral-200 dark:text-neutral-700 mx-auto mb-3" />
                  <p className="text-sm text-neutral-500">Noch keine E-Mails mit diesem Kontakt</p>
                  <p className="text-xs text-neutral-400 mt-1">Kampagnen und Posteingangs-Antworten erscheinen hier.</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {contextMenu?.contact && createPortal(
        <div
          className="fixed z-[200] min-w-[11rem] rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl py-1 scale-in"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="px-3 py-2 border-b border-neutral-100 dark:border-neutral-800">
            <p className="text-xs font-medium text-neutral-800 dark:text-neutral-100 truncate max-w-[10rem]">
              {contextMenu.contact.name || contextMenu.contact.email}
            </p>
          </div>
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
            onClick={() => {
              const c = contextMenu.contact;
              setContextMenu(null);
              openContactDetail(c);
            }}
          >
            <User className="w-4 h-4 text-neutral-400" />
            Öffnen
          </button>
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
            onClick={(e) => {
              const c = contextMenu.contact;
              setContextMenu(null);
              handleEdit(c, e);
            }}
          >
            <Edit className="w-4 h-4 text-neutral-400" />
            Bearbeiten
          </button>
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
            onClick={() => {
              const c = contextMenu.contact;
              setContextMenu(null);
              openContactHistory(c);
            }}
          >
            <Mail className="w-4 h-4 text-neutral-400" />
            E-Mail-Verlauf
          </button>
          <div className="my-1 border-t border-neutral-100 dark:border-neutral-800" />
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20"
            onClick={(e) => handleDelete(contextMenu.contact.id, e)}
          >
            <Trash2 className="w-4 h-4" />
            Löschen
          </button>
        </div>,
        document.body
      )}

      {contactsToDelete?.length ? createPortal(
        <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => !deletingContacts && setContactsToDelete(null)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    {contactsToDelete.length === 1 ? 'Kontakt löschen?' : `${contactsToDelete.length} Kontakte löschen?`}
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                    {contactsToDelete.length === 1 ? (
                      <>
                        Möchtest du{' '}
                        <span className="font-medium text-neutral-800 dark:text-neutral-200">
                          „{contactsToDelete[0].name || contactsToDelete[0].email}“
                        </span>{' '}
                        wirklich löschen?
                      </>
                    ) : (
                      <>
                        Möchtest du diese {contactsToDelete.length} Kontakte wirklich löschen?
                      </>
                    )}
                    {' '}
                    Zugehörige Kampagnen und geplante Follow-ups werden gestoppt. Bereits gesendete E-Mails bleiben erhalten.
                  </p>
                  {contactsToDelete.length > 1 && contactsToDelete.length <= 8 ? (
                    <ul className="mt-2 max-h-32 overflow-y-auto text-xs text-neutral-500 dark:text-neutral-400 space-y-0.5">
                      {contactsToDelete.map((c) => (
                        <li key={c.id} className="truncate">
                          {c.name || c.email}
                          {c.name && c.email ? ` · ${c.email}` : ''}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
              <button
                type="button"
                disabled={deletingContacts}
                onClick={confirmDeleteContacts}
                className="inline-flex items-center justify-center rounded-lg bg-danger-600 hover:bg-danger-700 text-white text-sm font-medium px-3.5 py-2 disabled:opacity-60"
              >
                {deletingContacts ? 'Löschen…' : 'Löschen'}
              </button>
              <button
                type="button"
                disabled={deletingContacts}
                onClick={() => setContactsToDelete(null)}
                className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-60"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      {showCategoryManager && createPortal(
        <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => {
              if (categoryManagerBusy) return;
              cancelEditCategory();
              setShowCategoryManager(false);
            }}
          />
          <div className="relative w-full max-w-md rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-neutral-900 dark:text-white">Kategorien</h2>
                <p className="text-xs text-neutral-500 mt-0.5">Für dieses Postfach · Mehrfachzuordnung möglich</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  cancelEditCategory();
                  setShowCategoryManager(false);
                }}
                className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Neue Kategorie
                </label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateCategory();
                    }
                  }}
                  className="input-field"
                  placeholder="z. B. Interessent, Kunde, Handwerk…"
                  maxLength={80}
                />
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORY_COLOR_PRESETS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewCategoryColor(color)}
                      className={`w-6 h-6 rounded-full border-2 ${
                        newCategoryColor === color
                          ? 'border-black scale-110'
                          : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  disabled={categoryManagerBusy}
                  onClick={handleCreateCategory}
                  className="btn-primary w-full justify-center"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Kategorie anlegen
                </button>
              </div>
              <div className="border-t border-neutral-100 dark:border-neutral-800 pt-4 space-y-2">
                {categories.length === 0 ? (
                  <p className="text-sm text-neutral-500 text-center py-4">Noch keine Kategorien</p>
                ) : (
                  categories.map((cat) => {
                    const isEditing = Number(editingCategoryId) === Number(cat.id);
                    if (isEditing) {
                      return (
                        <div
                          key={cat.id}
                          className="rounded-xl border border-primary-300 dark:border-primary-700 bg-primary-50/40 dark:bg-primary-950/20 px-3 py-2.5 space-y-2"
                        >
                          <input
                            type="text"
                            value={editCategoryName}
                            disabled={categoryManagerBusy}
                            onChange={(e) => setEditCategoryName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveCategory();
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelEditCategory();
                              }
                            }}
                            className="input-field !py-1.5 !text-sm"
                            placeholder="Kategoriename"
                            maxLength={80}
                            autoFocus
                          />
                          <div className="flex flex-wrap gap-1.5">
                            {CATEGORY_COLOR_PRESETS.map((color) => (
                              <button
                                key={color}
                                type="button"
                                disabled={categoryManagerBusy}
                                onClick={() => setEditCategoryColor(color)}
                                className={`w-6 h-6 rounded-full border-2 ${
                                  editCategoryColor === color
                                    ? 'border-black scale-110'
                                    : 'border-transparent'
                                }`}
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                            ))}
                          </div>
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              disabled={categoryManagerBusy}
                              onClick={cancelEditCategory}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-50"
                            >
                              <X className="w-3.5 h-3.5" />
                              Abbrechen
                            </button>
                            <button
                              type="button"
                              disabled={categoryManagerBusy || !String(editCategoryName || '').trim()}
                              onClick={handleSaveCategory}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                            >
                              <Check className="w-3.5 h-3.5" />
                              Speichern
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={cat.id}
                        className="flex items-center gap-2 rounded-xl border border-neutral-200 dark:border-neutral-700 px-3 py-2"
                      >
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span className="flex-1 text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                          {cat.name}
                        </span>
                        <button
                          type="button"
                          disabled={categoryManagerBusy}
                          onClick={() => startEditCategory(cat)}
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                          title="Bearbeiten"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          disabled={categoryManagerBusy}
                          onClick={() => handleDeleteCategory(cat)}
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                          title="Löschen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {historyEmailToDelete && createPortal(
        <div className="fixed inset-0 z-[230] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => !deletingHistoryEmail && setHistoryEmailToDelete(null)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    Gesendete E-Mail löschen?
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                    Diese E-Mail wird unwiderruflich aus dem Verlauf entfernt.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
              <button
                type="button"
                disabled={deletingHistoryEmail}
                onClick={confirmDeleteHistorySentEmail}
                className="inline-flex items-center justify-center rounded-lg bg-danger-600 hover:bg-danger-700 text-white text-sm font-medium px-3.5 py-2 disabled:opacity-60"
              >
                {deletingHistoryEmail ? 'Löschen…' : 'Löschen'}
              </button>
              <button
                type="button"
                disabled={deletingHistoryEmail}
                onClick={() => setHistoryEmailToDelete(null)}
                className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-60"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ContactImageImport
        open={showImageImport}
        onClose={() => setShowImageImport(false)}
        existingContacts={contacts}
        onImported={() => {
          fetchContacts();
          notifyContactsChanged();
        }}
      />

      <ContactCsvImport
        open={showCsvImport}
        rows={csvPreviewRows}
        fileName={csvFileName}
        mode={csvImportMode}
        onClose={() => {
          setShowCsvImport(false);
          setCsvPreviewRows(null);
          setCsvFileName('');
          setCsvImportMode('full');
        }}
        existingContacts={contacts}
        existingCategories={categories}
        onImported={() => {
          fetchContacts();
          notifyContactsChanged();
        }}
      />
    </div>
  );
};

export default Contacts;
